import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Printer } from "lucide-react";
import api from "../../lib/api";
import { fmtDate } from "../../lib/statuses";

export default function ShippingSlip() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/admin/shipping-batches/${id}`).then(({ data }) => setData(data)); }, [id]);

  if (!data) return <div className="p-10 text-center text-slate-400">Loading shipping slip...</div>;
  const { batch, sender } = data;
  const d = batch.destination || {};

  return (
    <div className="min-h-screen bg-slate-100 py-6 print:bg-white print:py-0">
      <div className="mx-auto mb-4 flex max-w-[210mm] items-center justify-between px-4 print:hidden">
        <Button variant="outline" size="sm" onClick={() => navigate("/admin/ready-to-ship")} className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <Button size="sm" onClick={() => window.print()} data-testid="print-slip-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700"><Printer className="h-4 w-4" /> Print / Save PDF</Button>
      </div>

      <div className="mx-auto w-[210mm] bg-white p-[14mm] text-slate-900 shadow-lg print:w-full print:shadow-none" data-testid="shipping-slip" id="slip">
        <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4">
          <div>
            <div className="text-2xl font-black tracking-tight">{sender?.brand_name || "IDEA OMAN"}</div>
            <div className="text-xs text-slate-500">{sender?.company_name}</div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold">SHIPPING SLIP</div>
            <div className="font-mono text-sm">{batch.ship_number}</div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-6 text-sm">
          <div>
            <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">From</div>
            <div className="font-semibold">{sender?.company_name}</div>
            <div>{sender?.contact_person}</div>
            <div>{sender?.phone}</div>
            <div>{[sender?.street, sender?.area].filter(Boolean).join(", ")}</div>
            <div>{[sender?.city, sender?.province, sender?.postal].filter(Boolean).join(", ")}</div>
            <div>{sender?.country}</div>
          </div>
          <div>
            <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">To</div>
            <div className="font-semibold">{d.supplier_name}</div>
            {d.rma_person && <div>Attn: {d.rma_person}</div>}
            {d.rma_contact && <div>{d.rma_contact}</div>}
            {d.rma_email && <div>{d.rma_email}</div>}
            <div>{[d.street, d.area].filter(Boolean).join(", ")}</div>
            <div>{[d.zip, d.province].filter(Boolean).join(", ")}</div>
            <div>{d.country}</div>
            {!(d.street || d.area || d.rma_person) && <div className="mt-1 text-xs font-semibold text-rose-600">⚠ No Service Centre address on file</div>}
          </div>
        </div>

        <div className="mt-4 text-sm"><span className="font-bold">Shipping Date:</span> {batch.shipping_date ? fmtDate(batch.shipping_date) : "____________________"}</div>

        <table className="mt-4 w-full border-collapse text-xs">
          <thead>
            <tr className="border-y-2 border-slate-900 text-left">
              <th className="py-2 pr-2 w-8">NO.</th>
              <th className="py-2 pr-2">SKU / PART NAME</th>
              <th className="py-2 pr-2">SERIAL NO.</th>
              <th className="py-2 pr-2 w-10 text-center">QTY</th>
              <th className="py-2 pr-2">FAULT REPORTED</th>
              <th className="py-2">APPROVAL / RMA STATUS</th>
            </tr>
          </thead>
          <tbody>
            {batch.items?.map((it) => (
              <tr key={it.no} className="border-b border-slate-200 align-top">
                <td className="py-2 pr-2">{it.no}</td>
                <td className="py-2 pr-2"><div className="font-semibold">{it.sku || it.part_name || it.product_name}</div><div className="text-slate-500">{it.part_name && it.sku ? it.part_name : it.product_name}</div><div className="font-mono text-[10px] text-slate-400">{it.rma_number}</div></td>
                <td className="py-2 pr-2 font-mono">{it.serial_number || "—"}</td>
                <td className="py-2 pr-2 text-center">{it.qty}</td>
                <td className="py-2 pr-2">{it.fault || "—"}</td>
                <td className="py-2">{it.approval_status || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {batch.notes && <div className="mt-4 text-xs"><span className="font-bold">Notes:</span> {batch.notes}</div>}

        <div className="mt-12 grid grid-cols-2 gap-10 text-sm">
          <div><div className="border-t border-slate-400 pt-1 text-xs text-slate-500">Prepared By — {batch.created_by}</div></div>
          <div><div className="border-t border-slate-400 pt-1 text-xs text-slate-500">Received By (Supplier)</div></div>
        </div>
      </div>
    </div>
  );
}
